import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { serverPHP, _key, debug_mode } from '../const/consts';

const SessionFooter = ({
  user, setUser, phase, redaction, currentPhase, setCurrentPhase,
  dureeSession, dernieremaj, goNext, usersInRoom, setUsersInRoom,
  setModalContent, setModalTitle, setShowModal, setEndSession,
  onToggleParticipants, isWaitingRoom = false
}) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [tickCount, setTickCount] = useState(0);
  const [timerSet, setTimerSet] = useState(false);
  const timerRef = useRef(null);

  const updateUsersInRoom = useCallback((newUsers) => {
    setUsersInRoom((prevUsers) => {
      const isEqual = prevUsers.length === newUsers.length &&
        prevUsers.every((prevUser, index) => {
          const newUser = newUsers[index];
          return prevUser.id === newUser.id &&
                 prevUser.isReady === newUser.isReady &&
                 prevUser.role === newUser.role &&
                 prevUser.name === newUser.name;
        });

      if (isEqual) {
        return prevUsers;
      }

      return newUsers;
    });
  }, [setUsersInRoom]);

  const fetchServerUpdate = useCallback(() => {
    fetch(`${serverPHP}getServerUpdate.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: user.currentsession,
        user_id: user.id
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        const currentTime = new Date(data.currentDateTime);
        const dernieremaj = new Date(data.dernieremaj);
        const elapsedTime = Math.floor((currentTime.getTime() - dernieremaj.getTime()) / 1000);

        if (dureeSession === 0) {
          setTimeRemaining(0);
        } else {
          setTimeRemaining(Math.max(dureeSession - elapsedTime, 0));
        }

        setTimerSet(true);

        if (data.users) {
          updateUsersInRoom(data.users);
        }

        if (data.phase !== undefined && data.phase !== currentPhase) {
          setCurrentPhase(data.phase);
        }

        if (data.closed === true) {
          if (debug_mode) console.log("Session fermée détectée");
          setEndSession(true);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch data:', error);
      });
  }, [user.currentsession, currentPhase, dureeSession, updateUsersInRoom, setCurrentPhase, setEndSession]);

  useEffect(() => {
    setTimeRemaining(null);
    fetchServerUpdate();
  }, [phase, fetchServerUpdate]);

  useEffect(() => {
    if (timeRemaining !== null) {
      timerRef.current = setInterval(() => {
        if (timeRemaining > 0) {
          setTimeRemaining(prevTime => Math.max(prevTime - 1, 0));
        } else if (timeRemaining === 0 && timerSet && dureeSession !== 0) {
          clearInterval(timerRef.current);
          if (debug_mode) console.log("GoNext à cause du timer");
          goNext();
        }
        if (tickCount % 5 === 0) {
          fetchServerUpdate();
        }
        setTickCount(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [timeRemaining, tickCount, fetchServerUpdate, goNext, timerSet, dureeSession]);

  useEffect(() => {
    if (usersInRoom.length > 0) {
      let usersToCheck;
      if (isWaitingRoom) {
        usersToCheck = usersInRoom; // Tous les utilisateurs en salle d'attente
      } else {
        usersToCheck = redaction ? usersInRoom.filter(user => user.role === 0) : usersInRoom.filter(user => user.role === 0 || user.role === 1);
      }
      
      if (usersToCheck.every(user => user.isReady) && dernieremaj) {
        if (debug_mode) console.log("goNext parce que tout le monde est prêt");
        goNext();
      }
    }
  }, [usersInRoom, redaction, goNext, dernieremaj, isWaitingRoom]);

  useEffect(() => {
    setUsersInRoom(prevUsers => prevUsers.map(user => ({ ...user, isReady: false })));
  }, [currentPhase, setUsersInRoom]);

  useEffect(() => {
    setUsersInRoom(prevUsers => {
      const updatedUsers = prevUsers.map(u => (u.id ^ _key === parseInt(user.id, 10) ? { ...u, role: user.role } : u));
      return updatedUsers;
    });
  }, [user.role, setUsersInRoom, user.id]);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (seconds) => {
    if (dureeSession === 0) {
      return "∞";
    }
    
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (dureeSession === 0) return '';
    if (timeRemaining <= 30) return 'timer-danger';
    if (timeRemaining <= 120) return 'timer-warning';
    return '';
  };

  const getReadyUsers = () => {
    if (isWaitingRoom) {
      return usersInRoom.filter(user => user.isReady);
    }
    return usersInRoom.filter(user => 
      (redaction ? user.role === 0 : (user.role === 0 || user.role === 1)) && user.isReady
    );
  };

  const getEligibleUsers = () => {
    if (isWaitingRoom) {
      return usersInRoom;
    }
    return usersInRoom.filter(user => 
      redaction ? user.role === 0 : (user.role === 0 || user.role === 1)
    );
  };

  const getSpectators = () => usersInRoom.filter(user => user.role === 2);
  const getEvaluators = () => usersInRoom.filter(user => user.role === 1);

  const readyUsers = getReadyUsers();
  const eligibleUsers = getEligibleUsers();
  const spectators = getSpectators();
  const evaluators = getEvaluators();

  // Calcul du pourcentage pour la progress bar (style original)
  const progressPercentage = dureeSession === 0 ? 0 : Math.max(0, ((dureeSession - (timeRemaining || 0)) / dureeSession) * 100);

  return (
    <>
      <div className="session-footer">
        <div className="footer-content">
          {/* Timer avec progress bar style original */}
          <div className="timer">
            <div className="progress-container-minimal">
              <div className="progress-bar-minimal">
                <div
                  className="progress-minimal"
                  style={{ width: `${100 - progressPercentage}%` }}
                ></div>
              </div>
              <span className={`time-remaining-minimal ${getTimerClass()}`}>
                {timeRemaining === null ? '--:--' : formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          
          {/* Statut participants */}
          <div className="btn btn-ghost btn-icon-only participants-status"
          onClick={() => window.dispatchEvent(new CustomEvent('toggleParticipantsPanel'))}>
            <div className="participants-count">
              👥 <span className="ready-count">{readyUsers.length}</span>/{eligibleUsers.length} {isWaitingRoom ? 'prêts' : 'prêts'}
              {!isWaitingRoom && (redaction && (evaluators.length > 0 || spectators.length > 0)) && (
                <span className="text-sm text-secondary ml-2">
                  ({evaluators.length > 0 && `${evaluators.length} évaluateur${evaluators.length > 1 ? 's' : ''}`}
                  {evaluators.length > 0 && spectators.length > 0 && ', '}
                  {spectators.length > 0 && `${spectators.length} spectateur${spectators.length > 1 ? 's' : ''}`})
                </span>
              )}
              {!isWaitingRoom && (!redaction && spectators.length > 0) && (
                <span className="text-sm text-secondary ml-2">
                  ({spectators.length} spectateur{spectators.length > 1 ? 's' : ''})
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default SessionFooter;